import { ModalInput } from "components/atoms/input/modal/ModalInput";
import { IoChevronDownOutline, IoChevronUp } from "react-icons/io5";

import { AdminUnlockStyled } from "./UtilityStyled";
import BigButton from "components/atoms/buttons/BigButton";
import SelectComponent from "components/atoms/select/SelectComponent";
import { ReactComponent as KEY_ADD_ICON } from "assets/icons/key_add.svg";
import MacAddressInput from "./MacAddressInput";
import { modelOptions } from "utils/options";
import { IoMdCopy } from "react-icons/io";
import { OtpRequest } from "api/interfaces/helpInterface.interface";
import Type from "components/atoms/text/labels/Type";
import { ValidateWord } from "components/atoms/text/Text";
import InputDate from "components/atoms/calendar/InputDate";
import TimeInput from "./TimeInput";
import CenterContentPage from "components/templates/layout/CenterContentPage";
import { ButtonWrapper } from "styles/ModalCommonStyled";
import { useAppSelector } from "redux/hooks";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useNavigate } from "react-router-dom";

type Props = {
  isCollapsed: boolean;
  formData: OtpRequest;
  otp: string;
  time: string;
  validateMsg: Record<string, string>;
  onClickGenterate: () => void;
  onClickCopy: () => void;
  onChange: ({ name, value }: { name: string; value: string }) => void;
  timezone: string;
  adminUnlockVersion: string;
};

export default function AdminUnlockPresenter(props: Props): JSX.Element {
  const otp = props.otp;
  const navigate = useNavigate();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  return (
    <CenterContentPage
      titleComponent="Admin Temporary Password Generator"
      titleDescription={
        <p>
          This service generates a temporary one-time password (OTP) that allows
          you to access your COVA/VMAX as the Admin user. <br />
          If you have forgotten the Admin password, simply enter your device’s
          MAC address and the current date on the recorder, and a valid
          temporary password will be issued for emergency access.
        </p>
      }
      buttons={
        <AdminUnlockStyled.ButtonWrapper>
          <ButtonWrapper className="button-wrapper">
            {selectedAccount.accountLevel !== LEVEL_TYPE.DW && (
              <BigButton
                label="Back"
                buttonType="cancel"
                onClickBtn={() => navigate(-1)}
                data-testid="maintenance-adminunlock-button-navigate"
              />
            )}

            <BigButton
              label="Generate OTP"
              onClickBtn={props.onClickGenterate}
              iconComponent={<KEY_ADD_ICON />}
              data-testid="maintenance-adminunlock-button-click-genterate"
            />
          </ButtonWrapper>

          {otp !== "" && (
            <AdminUnlockStyled.Otp>
              {otp}
              <button
                onClick={props.onClickCopy}
                data-testid="maintenance-adminunlock-button-click-copy"
              >
                <IoMdCopy size={22} />
              </button>
            </AdminUnlockStyled.Otp>
          )}
        </AdminUnlockStyled.ButtonWrapper>
      }
    >
      <AdminUnlockStyled.Container>
        <AdminUnlockStyled.ToggleText
          onClick={() => props.onChange({ name: "collapsed", value: "" })}
          data-testid="maintenance-adminunlock-toggle-text-change"
        >
          {props.isCollapsed ? <IoChevronUp /> : <IoChevronDownOutline />}
          How to retrieve the MAC address and recorder date information
        </AdminUnlockStyled.ToggleText>
        {props.isCollapsed && (
          <AdminUnlockStyled.RetrieveInfoWrapper>
            <li>
              <Type type="COVA" />
              <div>
                On the login screen, click the <b>"Retrieve Password"</b>{" "}
                button.
              </div>
            </li>
            <li>
              <Type type="NVR" displayType="VMAX" />
              <div>
                On the login screen, enter <b>"admin"</b> as the USER ID and{" "}
                <b>"masterpwd"</b> as the PASSWORD.
              </div>
            </li>
          </AdminUnlockStyled.RetrieveInfoWrapper>
        )}

        <AdminUnlockStyled.FormWrapper>
          <ModalInput label="Device">
            <div className="device-radio">
              <label>
                <input
                  type="radio"
                  name="device"
                  value="COVA"
                  checked={props.formData.device === "COVA"}
                  onChange={(e) => {
                    props.onChange(e.target);
                  }}
                  data-testid="maintenance-adminunlock-device-cova"
                />
                COVA
              </label>
              <label>
                <input
                  type="radio"
                  name="device"
                  value="VMAX"
                  checked={props.formData.device === "VMAX"}
                  onChange={(e) => {
                    props.onChange(e.target);
                  }}
                  data-testid="maintenance-adminunlock-device-vmax"
                />
                VMAX
              </label>
            </div>
          </ModalInput>
          {props.formData.device === "COVA" && (
            <ModalInput label="Model">
              <div className="form-input-inner">
                <SelectComponent
                  options={modelOptions}
                  defaultValue={modelOptions[0]}
                  onChange={(value: string) => {
                    props.onChange({ name: "model", value });
                  }}
                  data-testid="maintenance-adminunlock-selects-change"
                />

                {props.adminUnlockVersion && (
                  <ValidateWord>
                    * Only compatible with version {props.adminUnlockVersion} or
                    later versions.
                  </ValidateWord>
                )}
              </div>
            </ModalInput>
          )}

          <ModalInput label="MAC Address">
            <div className="form-input-inner">
              <MacAddressInput
                onChange={(value: string) => {
                  props.onChange({ name: "macAddress", value });
                }}
                data-testid="maintenance-adminunlock-mac-address"
              />

              {props.validateMsg.macAddress !== "" && (
                <ValidateWord data-testid="maintenance-adminunlock-mac-address-validate">
                  {props.validateMsg.macAddress}
                </ValidateWord>
              )}
            </div>
          </ModalInput>
          <ModalInput label="Recorder Date">
            <InputDate
              value={props.formData.expireDate}
              onChange={(value) => {
                props.onChange({
                  name: "expireDate",
                  value: value as string,
                });
              }}
            />
          </ModalInput>
          {props.formData.device === "COVA" && (
            <ModalInput label="Time">
              <div className="form-input-inner">
                <TimeInput
                  onChange={(value: string) => {
                    props.onChange({ name: "time", value });
                  }}
                  data-testid="maintenance-adminunlock-time"
                />

                {/* <Input
                name="time"
                value={props.time}
                placeholder="hh:mm:ss.zzz"
                onChange={(e) => {
                props.onChange(e.target);
                }}
                onBlur={props.handleBlur}
                /> */}
                {props.validateMsg.time !== "" && (
                  <ValidateWord data-testid="maintenance-adminunlock-time-validate">
                    {props.validateMsg.time}
                  </ValidateWord>
                )}
              </div>
            </ModalInput>
          )}
        </AdminUnlockStyled.FormWrapper>
      </AdminUnlockStyled.Container>
    </CenterContentPage>
  );
}
